import React, { createRef } from 'react'
import { I18n } from 'react-polyglot'
import Script from 'next/script'

import { GoogleTagManager } from '@next/third-parties/google'

import App from 'next/app'
import Error from 'next/error'
import dynamic from 'next/dynamic'
import { Poppins, Inter } from 'next/font/google'

import delve from 'dlv'
import * as Sentry from '@sentry/node'

import fetchSingleType from 'src/lib/Strapi/fetchSingleType'

import Page from 'components/shared/Page'
import PrideMonthBanner from 'components/shared/PrideMonthBanner'
import HasScrolledProvider, {
  DetectHasScrolled,
} from 'components/shared/HasScrolledProvider'
import HasStickyBarProvider from 'components/shared/HasStickyBarProvider'
import { BannerProvider } from 'src/components/shared/BannerProvider'

import 'reset-css/sass/_reset.scss'
import '../cookiebot-overrides.scss'
import '../marketing-popins.scss'
import '../styles/shiki-highlighter.scss'

import css from '../app.scss'
import { fetchGithubData } from 'src/lib/github'
import { transformDictionary } from '../services/dictionary'

// FIXME - Should use @sentry/nextjs + moved to instrumentation.js
const SENTRY_DSN = process.env.SENTRY_DSN

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    enabled: process.env.NODE_ENV === 'production',
    environment: process.env.ENVIRONMENT,
    tracesSampleRate: 0.1,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Action timed out',
      '3000ms timeout exceeded',
      "undefined is not an object (evaluating 'window.HubSpotConversations.resetAndReloadWidget')",
      "Cannot read properties of null (reading 'readyState')",
    ],
  })
}

/**
 * Initialize Redis pub/sub for ISR cache synchronization across K8s replicas.
 * Runs once at module load time on the server.
 *
 * FIXME - This should be replace by shared redis-cache or instrumentation in
 * newer versions of Next.js
 */
if (typeof window === 'undefined') {
  const revalidatePath = async (path) => {
    const port = process.env.PORT || 3000
    const url = `http://127.0.0.1:${port}/api/internal-revalidate?path=${encodeURIComponent(
      path
    )}`
    const headers = {
      'x-internal-revalidate': process.env.REVALIDATION_SECRET || '',
    }

    try {
      const res = await fetch(url, { method: 'GET', headers })
      const data = await res.text()
      return { status: res.status, data }
    } catch (error) {
      throw error
    }
  }

  try {
    const { PubSubClient } = await import('src/lib/redis/pubsub-client.js')
    const { subscribeToRevalidation } = await import(
      'src/lib/redis/revalidation.js'
    )

    console.log('Initializing Redis PubSubClient...')
    await PubSubClient.getInstance().connect()

    await subscribeToRevalidation(async (path) => {
      console.log(`Received revalidation for path: ${path}`)

      try {
        // Clear Algolia cache before revalidating
        const { clearAlgoliaCache } = await import('src/services/algolia.js')
        clearAlgoliaCache()

        await revalidatePath(path)
        console.log(`Successfully revalidated: ${path}`)
      } catch (error) {
        console.error(`Error revalidating ${path}:`, error.message)
      }
    })

    console.log('Redis subscription initialized')
  } catch (error) {
    console.error('Failed to initialize Redis:', error.message)
  }
}

/**
 * Dynamic imports for conditional pages - reduces initial bundle size.
 */
const OrderConfirmation = dynamic(() => import('./order-confirmation'), {
  ssr: false,
})
const GetLicense = dynamic(() => import('./get-license'), { ssr: false })

const poppins = Poppins({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-poppins',
})

const inter = Inter({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-inter',
})

if (typeof window === 'undefined') {
  const originalError = console.error
  console.error = (...args) => {
    if (/Warning: useLayoutEffect does nothing on the server/.test(args[0])) {
      return // Suppress the specific useLayoutEffect warning
    }
    originalError.call(console, ...args) // Allow other warnings to be shown
  }
}

export default class CustomApp extends App {
  pageRef = createRef()

  mergeGlobalDataWithSlices(slices) {
    return slices.map((slice) => {
      switch (slice.__component) {
        case 'slices.newsletter-banner':
        case 'slices.socials-grid':
          if (slice.newsletter) return slice

          return { ...slice, newsletter: this.props.newsletter }
        default:
          return slice
      }
    })
  }

  hasStickyBar(route) {
    switch (route) {
      case '/blog':
      case '/blog/[slug]':
      case '/blog/categories/[slug]':
      case '/resource-center':
        return true
      default:
        return false
    }
  }

  getPageSettings(pageSettings, router) {
    if (router.route.startsWith('/marketplace')) {
      return {
        ...pageSettings,
        logo: {
          local: true,
          media: { url: '/assets/marketplace_logo.svg' },
          alt: 'Strapi Market logo',
        },
        isMarketplace: true,
      }
    }

    if (router.route.startsWith('/launch-week')) {
      return {
        ...pageSettings,
        logo: {
          local: true,
          media: { url: '/assets/strapi-logo-white.svg' },
          alt: 'Strapi logo',
        },
        isLaunchWeek: true,
      }
    }

    return pageSettings
  }

  renderContent() {
    const {
      Component,
      logoPopin,
      header,
      footer,
      newsletter,
      pageProps,
      seo,
      banner,
      prideMonth,
      router,
      githubStars,
    } = this.props

    const pageSettings = delve(pageProps, 'pageData.settings')
    const createdAt = delve(pageProps, 'pageData.created_at', '')
    const updatedAt = delve(pageProps, 'pageData.updated_at', '')
    const slices = delve(pageProps, 'pageData.slices')
    const pageSeo = pageProps?.pageData?.SEO
      ? delve(pageProps, 'pageData.SEO')
      : delve(pageProps, 'pageData.seo')
    const statusCode = delve(pageProps, 'statusCode')
    const darkMode = delve(pageProps, 'pageData.darkMode')

    if (pageProps?.pageData && slices) {
      pageProps.pageData.slices = this.mergeGlobalDataWithSlices(slices)
    }

    if (pageProps?.pageData && newsletter) {
      pageProps.pageData.newsletter = newsletter
    }

    const currentRoute = router.route

    // Handle standalone pages without navigation/footer
    if (currentRoute.startsWith('/order-confirmation')) {
      return <OrderConfirmation />
    }

    if (currentRoute.startsWith('/get-license')) {
      return <GetLicense />
    }

    // Handle 404 errors
    if (statusCode && statusCode === 404) {
      return <Error statusCode={statusCode} />
    }

    const settings = this.getPageSettings(pageSettings, router)
    const showPrideMonthBanner =
      prideMonth &&
      !currentRoute.startsWith('/marketplace') &&
      !currentRoute.startsWith('/launch-week')

    return (
      <div>
        <Page
          githubStars={githubStars}
          header={header}
          banner={banner}
          logoPopin={logoPopin}
          footer={footer}
          newsletter={newsletter}
          settings={settings}
          pageSeo={{
            ...pageSeo,
            createdAt,
            updatedAt,
          }}
          seo={seo}
          forwardRef={this.pageRef}
          darkMode={darkMode}
        >
          <Component {...pageProps} />
        </Page>
        {showPrideMonthBanner && <PrideMonthBanner />}
      </div>
    )
  }

  render() {
    const { dictionary, router } = this.props

    const dictionaryData = transformDictionary(dictionary)

    return (
      <>
        <style jsx global>
          {`
            :root {
              --font-poppins: ${poppins.style.fontFamily};
              --font-inter: ${inter.style.fontFamily};
            }

            html {
              font-family: ${router.route.startsWith('/launch-week')
                ? inter.style.fontFamily
                : poppins.style.fontFamily};
            }
          `}
        </style>
        <GoogleTagManager gtmId="GTM-KN9JRWG" />

        {process.env.NEXT_PUBLIC_KAPA_WEBSITE_ID && (
          <Script
            id="kapa-widget"
            src="https://widget.kapa.ai/kapa-widget.bundle.js"
            data-website-id={process.env.NEXT_PUBLIC_KAPA_WEBSITE_ID}
            data-project-name="Strapi"
            data-project-logo="https://strapi.io/assets/favicon-32x32.png"
            data-modal-disclaimer="Disclaimer: Answers are AI-generated and might be inaccurate. Please ensure you double-check the information provided by visiting source pages."
            data-project-color="#4945FF"
            data-button-bg-color="#32324D"
            data-modal-example-questions="How to create a Strapi project?,How does population work?,How to customize the admin panel?,Explain the Growth plan benefits"
            data-modal-title-ask-ai="Ask your question"
            data-modal-border-radius="4px"
            data-submit-query-button-bg-color="#4945FF"
            data-modal-body-padding-top="20px"
            data-user-analytics-cookie-enabled="true"
            data-modal-size="900px"
            data-modal-full-screen="false"
            data-modal-full-screen-on-mobile="true"
            data-modal-x-offset="0"
            data-modal-y-offset="3vh"
            data-modal-inner-max-width="100%"
            data-modal-z-index="10000"
            strategy="lazyOnload"
          />
        )}

        <Script
          data-cookieconsent="marketing"
          id="hs-script-loader"
          src="//js.hs-scripts.com/6893032.js"
          strategy="afterInteractive"
        />

        <Script id="hotjar" strategy="afterInteractive">
          {`
            (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:1095300,hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}
        </Script>

        <HasScrolledProvider>
          <HasStickyBarProvider
            initialHasStickyBar={this.hasStickyBar(router.route)}
          >
            <I18n locale="en" messages={dictionaryData} allowMissing>
              <div className={css.App}>
                <DetectHasScrolled />
                <BannerProvider>{this.renderContent()}</BannerProvider>
              </div>
            </I18n>
          </HasStickyBarProvider>
        </HasScrolledProvider>
      </>
    )
  }
}

CustomApp.getInitialProps = async (context) => {
  const ctx = await App.getInitialProps(context)

  // Helper function to fetch data with retry logic
  const fetchDataWithRetry = async (retry = true) => {
    const data = await fetchSingleType('global')

    // Check if header is defined in the data
    if (data?.header || !retry) {
      return data
    } else {
      // Log the retry attempt
      console.log(
        'Retrying to fetch global singleType due to getInitialProps issue...'
      )
      return fetchDataWithRetry(false) // Retry once
    }
  }

  // Fetch data with retry logic
  const [data, githubData] = await Promise.all([
    fetchDataWithRetry(),
    fetchGithubData(),
  ])
  const githubStars = githubData?.stargazers_count
  const {
    header,
    footer,
    dictionary,
    newsletter,
    seo,
    metaTitleSuffix,
    banner,
    prideMonth,
    logoPopin,
  } = data || {} // Fallback to empty object if data is undefined

  return {
    ...ctx,
    header,
    footer,
    dictionary,
    newsletter,
    logoPopin,
    seo: {
      ...seo,
      metaTitleSuffix,
    },
    banner,
    prideMonth,
    githubStars,
  }
}
